import chatApi from '@/modules/chat/api/chat'
import store from '@/store'

/**
 * Maximum number of reactions a single user can add to a message
 */
const MAX_REACTIONS_PER_USER = 5

/**
 * Default retry configuration for saving reactions
 */
const RETRY_CONFIG = {
  maxAttempts: 3,
  delayMs: 500,
}

/**
 * Sleep helper for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} Promise that resolves after the delay
 */
function sleep(ms) {
  return new Promise((resolve) => { setTimeout(resolve, ms) })
}

/**
 * Get the current user's ID
 * @returns {number} The current user's ID
 */
function getCurrentUserId() {
  return store.getters['auth/userId']
}

/**
 * Parse reactions from message metadata
 * @param {Object} message - The message object
 * @returns {Array} Array of [userId, emoji] tuples
 */
export function parseReactions(message) {
  if (!message?.metadata?.reactions) {
    return []
  }
  try {
    // Reactions are stored as JSON string in metadata
    const reactions = typeof message.metadata.reactions === 'string'
      ? JSON.parse(message.metadata.reactions)
      : message.metadata.reactions
    return Array.isArray(reactions) ? reactions : []
  } catch {
    return []
  }
}

/**
 * Get all reactions for a specific user on a message
 * @param {Object} message - The message object
 * @param {number} userId - The user ID to filter by (defaults to current user)
 * @returns {Array} Array of emoji strings the user has reacted with
 */
export function getUserReactionsForMessage(message, userId = null) {
  const targetUserId = userId ?? getCurrentUserId()
  const reactions = parseReactions(message)
  return reactions
    .filter(([uid]) => Number(uid) === Number(targetUserId))
    .map(([, emoji]) => emoji)
}

/**
 * Check if a user can add another reaction to a message
 * @param {Object} message - The message object
 * @param {number} userId - The user ID to check (defaults to current user)
 * @returns {boolean} True if the user can add more reactions
 */
export function canUserAddReaction(message, userId = null) {
  const userReactions = getUserReactionsForMessage(message, userId)
  return userReactions.length < MAX_REACTIONS_PER_USER
}

/**
 * Check if a user has already reacted with a specific emoji
 * @param {Object} message - The message object
 * @param {string} emoji - The emoji to check
 * @param {number} userId - The user ID to check (defaults to current user)
 * @returns {boolean} True if the user has already reacted with this emoji
 */
export function hasUserReactedWithEmoji(message, emoji, userId = null) {
  const userReactions = getUserReactionsForMessage(message, userId)
  return userReactions.includes(emoji)
}

/**
 * Remove a reaction from a message's reactions array
 * @param {Object} message - The message object
 * @param {string} emoji - The emoji to remove
 * @param {number} userId - The user ID (defaults to current user)
 * @returns {Array} New reactions array without the removed reaction
 */
export function removeReaction(message, emoji, userId = null) {
  const targetUserId = userId ?? getCurrentUserId()
  const reactions = parseReactions(message)

  return reactions.filter(([uid, e]) => !(Number(uid) === Number(targetUserId) && e === emoji))
}

/**
 * Add a reaction to a message's reactions array
 * @param {Object} message - The message object
 * @param {string} emoji - The emoji to add
 * @param {number} userId - The user ID (defaults to current user)
 * @returns {Array} New reactions array with the added reaction
 */
export function addReaction(message, emoji, userId = null) {
  const targetUserId = userId ?? getCurrentUserId()
  const reactions = parseReactions(message)

  // Check if user already reacted with this emoji (toggle off)
  if (hasUserReactedWithEmoji(message, emoji, targetUserId)) {
    return removeReaction(message, emoji, targetUserId)
  }

  // Check if user has reached the limit
  if (!canUserAddReaction(message, targetUserId)) {
    return reactions
  }

  // Add new reaction at the end
  return [...reactions, [targetUserId, emoji]]
}

/**
 * Add a reaction via backend API with retry logic
 * @param {string} chatThreadId - The Azure chat thread ID
 * @param {string} messageId - The message ID
 * @param {string} emoji - The emoji to add
 * @returns {Promise<boolean>} True if save was successful, false otherwise
 */
export async function addReactionToBackend(chatThreadId, messageId, emoji) {
  // eslint-disable-next-line no-await-in-loop
  for (let attempt = 1; attempt <= RETRY_CONFIG.maxAttempts; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await chatApi.addReaction(chatThreadId, messageId, emoji)
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Add reaction attempt ${attempt}/${RETRY_CONFIG.maxAttempts} failed:`, error)

      if (attempt < RETRY_CONFIG.maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(RETRY_CONFIG.delayMs * attempt)
      }
    }
  }

  return false
}

/**
 * Remove a reaction via backend API with retry logic
 * @param {string} chatThreadId - The Azure chat thread ID
 * @param {string} messageId - The message ID
 * @param {string} emoji - The emoji to remove
 * @returns {Promise<boolean>} True if removal was successful, false otherwise
 */
export async function removeReactionFromBackend(chatThreadId, messageId, emoji) {
  // eslint-disable-next-line no-await-in-loop
  for (let attempt = 1; attempt <= RETRY_CONFIG.maxAttempts; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await chatApi.removeReaction(chatThreadId, messageId, emoji)
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Remove reaction attempt ${attempt}/${RETRY_CONFIG.maxAttempts} failed:`, error)

      if (attempt < RETRY_CONFIG.maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(RETRY_CONFIG.delayMs * attempt)
      }
    }
  }

  return false
}

/**
 * Get the count of reactions for a specific user on a message
 * @param {Object} message - The message object
 * @param {number} userId - The user ID (defaults to current user)
 * @returns {number} Number of reactions by this user
 */
export function getUserReactionCount(message, userId = null) {
  return getUserReactionsForMessage(message, userId).length
}

export default {
  parseReactions,
  getUserReactionsForMessage,
  canUserAddReaction,
  hasUserReactedWithEmoji,
  addReaction,
  removeReaction,
  addReactionToBackend,
  removeReactionFromBackend,
  getUserReactionCount,
  MAX_REACTIONS_PER_USER,
}
